import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, Outlet, RouteObject, RouterProvider } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Toaster } from 'react-hot-toast';

import * as Sentry from '@sentry/react';
import { init as initFullStory } from '@fullstory/browser';
import { StytchB2BProvider } from '@stytch/react/b2b';
import { StytchB2BUIClient } from '@stytch/vanilla-js/b2b';

import { FeatureFlags, useFeatureFlags } from './hooks/useFeatureFlags';

import 'unfonts.css';
import { LoginProvider, LoginRequired } from './auth';
import { Login, PasswordReset } from './components/StytchLogin';
import DashboardEmbed from './components/DashboardEmbed';
import NewTaskModal from './components/tasks/NewTaskModal';

import RxTablePage from './pages/RxTablePage';
import ExpiringPriorAuthTablePage from './pages/ExpiringPriorAuthTablePage';
import FaxVerificationPage from './pages/FaxVerificationPage';
import HelpCenter from './pages/HelpCenter/HelpCenter';
import { PendingEnrollmentPage } from './pages/PendingEnrollmentPage';
import NotificationsTablePage from './pages/NotificationsTablePage';
import NewTaskPage from './pages/HelpCenter/NewTaskPage';
import ReferralPage from './pages/ReferralPage';
import PriorAuthReviewPage from './pages/PriorAuthReviewPage';
import PriorAuthReviewsTablePage from './pages/PriorAuthReviewsTablePage';
import RxPage from './pages/RxPage';
import SettingsPage from './pages/SettingsPage';
import SignupDocumentPage from './pages/LegacySignupDocumentPage';
import LegacySignupPage from './pages/LegacySignupPage';
import TaskPage from './pages/TaskPage';
import TremfyaExpressEnrollmentLoggedOutTaskPage from './pages/TremfyaExpressEnrollmentLoggedOutTaskPage';
import TasksTablePage from './pages/TasksTablePage';
import OnboardingPage from './pages/OnboardingPage';
import SignupInitialPage from './pages/Signup/SignupInitialPage';
import SignupInitialContent from './pages/Signup/SignupInitialContent';
import SignupStartPage from './pages/Signup/SignupStartPage';
import PracticeInfoPage from './pages/Signup/PracticeInfoPage';
import ProfileInfoPage from './pages/Signup/ProfileInfoPage';
import ScheduleDemoPage from './pages/Signup/ScheduleDemoPage';
import ConfirmationStepPage from './pages/Signup/ConfirmationStepPage';
import VerificationEmailPage from './pages/Signup/VerificationEmailPage';
import PasswordSetupPage from './pages/Signup/PasswordSetupPage';
import PasswordSetupFormPage from './pages/Signup/PasswordSetupFormPage';
import TermsOfServicePage from './pages/Signup/TermsOfServicePage';
import AddProviderPage from './pages/Signup/AddProviderAndPhoneNumberPage';
import { FormPage } from './pages/FormPage';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import SignupSuccessPage from './pages/Signup/SignupSuccessPage';
import { Suspense } from 'react';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { ErrorBoundary } from '@sentry/react';

Sentry.init({
  dsn: import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ['partner.withtandem.com'],
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0, // Only capture sessions with errors - other sessions are in Fullstory already
  replaysOnErrorSampleRate: 1.0, // Capture 100% of sessions with errors
});

/**
 * FullStory -- collect sessions from production only
 */
if (import.meta.env.MODE === 'production') {
  initFullStory({ orgId: import.meta.env.VITE_FULLSTORY_ORG_ID });
}

const queryClient = new QueryClient();
queryClient.setDefaultOptions({
  queries: {
    refetchOnWindowFocus: false,
    refetchInterval: false,
  },
});

const stytch = new StytchB2BUIClient(import.meta.env.VITE_STYTCH_KEY);

const createRoutes = (featureFlags: FeatureFlags | undefined) => {
  const signupOrDemoFlowEnabled =
    featureFlags?.providerSignupFlow || featureFlags?.providerDemoFlow || featureFlags?.providerSplitSignup;
  const signupFlowEnabled = featureFlags?.providerSignupFlow || featureFlags?.providerSplitSignup;
  const onboardingFlowEnabled = featureFlags?.providerOnboarding;
  const routes: RouteObject[] = [
    {
      // This signup page is used for enterprise users to just sign the terms of service, independent from setting up a practice
      path: '/signup-page',
      element: <LegacySignupPage />,
    },
    {
      path: '/signup',
      element: signupOrDemoFlowEnabled ? <SignupInitialPage /> : <LegacySignupPage />,
      children: [
        {
          index: true,
          element: signupOrDemoFlowEnabled ? <SignupInitialContent /> : <LegacySignupPage />,
        },
        // If only demo is enabled, user navigates to /signup/start directly
        {
          path: 'start',
          element: signupOrDemoFlowEnabled ? <SignupStartPage /> : <LegacySignupPage />,
        },
        {
          path: 'practice-info',
          element: signupOrDemoFlowEnabled ? <PracticeInfoPage /> : <LegacySignupPage />,
        },
        {
          path: 'profile-info',
          element: signupOrDemoFlowEnabled ? <ProfileInfoPage /> : <LegacySignupPage />,
        },
        {
          path: 'success',
          element: signupOrDemoFlowEnabled ? <SignupSuccessPage /> : <LegacySignupPage />,
        },
        {
          path: 'schedule-demo',
          element: signupOrDemoFlowEnabled ? <ScheduleDemoPage /> : <LegacySignupPage />,
        },
        {
          path: 'confirmation-step',
          element: signupOrDemoFlowEnabled ? <ConfirmationStepPage /> : <LegacySignupPage />,
        },
        {
          // If signup flow is not enabled, user should not be able to continue past demo booking
          path: 'verification-email',
          element: signupFlowEnabled ? <VerificationEmailPage /> : <LegacySignupPage />,
        },
      ],
    },
    {
      path: '/demo',
      element: <SignupInitialPage />,
      children: [
        {
          index: true,
          element: <SignupStartPage />,
        },
        {
          path: 'profile-info',
          element: <ProfileInfoPage />,
        },
        {
          path: 'practice-info',
          element: <PracticeInfoPage />,
        },
        {
          path: 'success',
          element: <SignupSuccessPage />,
        },
        {
          path: 'schedule-demo',
          element: <ScheduleDemoPage />,
        },
        {
          path: 'confirmation-step',
          element: <ConfirmationStepPage />,
        },
      ],
    },
    {
      path: '/account-setup',
      element: signupFlowEnabled ? (
        <LoginRequired>
          <PasswordSetupPage />
        </LoginRequired>
      ) : (
        <LegacySignupPage />
      ),
      children: signupFlowEnabled
        ? [
            {
              index: true,
              element: <PasswordSetupFormPage />,
            },
            {
              path: 'terms-of-service',
              element: <TermsOfServicePage />,
            },
            {
              path: 'add-provider',
              element: <AddProviderPage />,
            },
          ]
        : undefined,
    },
    {
      path: '/onboarding',
      element: onboardingFlowEnabled ? (
        <LoginRequired>
          <OnboardingPage />
        </LoginRequired>
      ) : (
        <Navigate to="/" replace />
      ),
    },
    {
      path: '/tos-full',
      element: <SignupDocumentPage />,
    },
    {
      path: '/login',
      element: <Login />,
    },
    {
      path: '/login/:slug',
      element: <Login />,
    },
    {
      path: '/password-reset',
      element: <PasswordReset />,
    },
    {
      path: '/analytics',
      element: (
        <LoginRequired>
          <DashboardEmbed />
        </LoginRequired>
      ),
    },
    {
      path: '/',
      element: <Navigate to="/prescriptions" replace />,
    },
    // Keeping both routes in case we want to explicitly link to /prescriptions
    {
      path: '/prescriptions',
      element: (
        <LoginRequired>
          <RxTablePage />
        </LoginRequired>
      ),
    },
    {
      path: '/tasks',
      element: (
        <LoginRequired>
          <TasksTablePage />
        </LoginRequired>
      ),
      children: [
        {
          path: 'new',
          element: <NewTaskModal />,
        },
      ],
    },
    {
      path: '/tasks/:taskId',
      element: (
        <LoginRequired>
          <TaskPage />
        </LoginRequired>
      ),
    },
    {
      path: '/tremfya-enrollment/:token',
      element: <TremfyaExpressEnrollmentLoggedOutTaskPage />,
    },
    {
      path: '/notifications',
      element: (
        <LoginRequired>
          <NotificationsTablePage />
        </LoginRequired>
      ),
    },
    {
      path: '/expiring-prior-auths',
      element: (
        <LoginRequired>
          <ExpiringPriorAuthTablePage />
        </LoginRequired>
      ),
    },
    {
      path: '/prior-auth-reviews',
      element: (
        <LoginRequired>
          <PriorAuthReviewsTablePage />
        </LoginRequired>
      ),
    },
    {
      path: '/prior-auth-reviews/:priorAuthReviewId',
      element: (
        <LoginRequired>
          <PriorAuthReviewPage />
        </LoginRequired>
      ),
    },
    {
      path: '/rxs/:rxId',
      element: (
        <LoginRequired>
          <RxPage />
        </LoginRequired>
      ),
      children: [
        {
          path: 'new-task',
          element: <NewTaskModal />,
        },
      ],
    },
    {
      path: '/settings',
      element: (
        <LoginRequired>
          <SettingsPage />
        </LoginRequired>
      ),
    },
    {
      path: '/help-center',
      element: (
        <LoginRequired>
          <HelpCenter />
        </LoginRequired>
      ),
      children: [
        {
          path: 'new-task',
          element: <NewTaskPage />,
        },
      ],
    },
    {
      path: '/referral',
      element: (
        <LoginRequired>
          <ReferralPage />
        </LoginRequired>
      ),
    },
    {
      path: '/verify-fax',
      element: (
        <LoginRequired>
          <FaxVerificationPage />
        </LoginRequired>
      ),
    },
    {
      path: '/enrollments/pending/:enrollmentId',
      element: (
        <LoginRequired>
          <PendingEnrollmentPage />
        </LoginRequired>
      ),
    },
    {
      // Unauthenticated route with a `token` query parameter
      path: '/forms',
      element: <FormPage />,
    },
  ];

  return routes;
};

function AppWithFeatureFlags() {
  const { data: featureFlags, isLoading, isError, error } = useFeatureFlags();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    // Feature flags are not critical to the app, so we can continue without them
    Sentry.captureException(error);
  }

  const routes = createRoutes(featureFlags);
  /**
   * This applies the error boundary + suspense boundary inside the router across
   * all our routes to intercept errors before they hit the React Router error element.
   */
  const router = createBrowserRouter([
    {
      element: (
        <ErrorBoundary fallback={<PageLoadError />}>
          <Suspense fallback={<LoadingSpinner />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      ),
      children: routes,
    },
  ]);

  return <RouterProvider router={router} />;
}

function App() {
  return (
    <StytchB2BProvider stytch={stytch}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <LoginProvider>
            <AppWithFeatureFlags />
            <Toaster position="bottom-right" toastOptions={{ duration: 3000 }} reverseOrder={true} />
          </LoginProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </StytchB2BProvider>
  );
}

export default App;
